<template>
	<div class="eventium-production-box" :data-event-type="attributes.type">
		<EventiumBoxBackground
			:skin="attributes.type ?? EventCategory.CHRISTMAS"
		/>
		<div class="eventium-production-box__icons">
			<div
				v-for="(icon, i) in tileIcons"
				:key="i"
				class="eventium-production-box__tile-icon"
				:class="`icon-${icon}${rare ? '-rare' : ''}-32`"
			>
				<div
					v-if="isCollectableEvent(attributes.type)"
					class="clicker-icon icon-clicker-32"
				/>
			</div>
			<div class="eventium-production-box__arrow arrow--right-18" />
			<div
				v-if="incomeValue"
				class="eventium-production-box__income"
				:class="{ rare }"
			>
				{{ `+${incomeValue}` }}
			</div>
			<div :class="`icon-${eventStore.attributes.type}-eventium-32`" />
		</div>
	</div>
</template>

<script lang="ts" setup>
import EventiumBoxBackground from "@/components/SVG/Event/EventiumBoxBackground.vue";
import {
	CollectableType,
	getEventCollectableConfig,
	isCollectableEvent,
	useEventStore,
} from "@/event";
import { FrameName } from "@/game/entities/config";
import { EventCategory } from "@/types/ui";
import { getTileIconName } from "@/utils/tileIconName";
import { storeToRefs } from "pinia";
import { computed } from "vue";

interface Props {
	rare?: boolean;
}

const { rare = false } = defineProps<Props>();

const eventStore = useEventStore();
const { attributes } = storeToRefs(eventStore);

const tileIcons = computed((): Array<string> => {
	if (isCollectableEvent(attributes.value.type)) {
		return [`tile-${attributes.value.type}-collectable`];
	}
	if (attributes.value.type === EventCategory.CHRISTMAS) {
		return [getTileIconName(FrameName.DARKWOOD_LUMBER_MILL)];
	}
	if (attributes.value.type === EventCategory.FISHING) {
		return [
			getTileIconName(FrameName.RAFT),
			getTileIconName(FrameName.BOTTLETRAP),
		];
	}
	if (attributes.value.type === EventCategory.FIREFEST) {
		return [getTileIconName(FrameName.FIRE_FESTIVAL_PYRE)];
	}
	if (attributes.value.type === EventCategory.SOLAR) {
		return [getTileIconName(FrameName.BOUNTIFUL_MARKETPLACE)];
	}
	if (attributes.value.type === EventCategory.HUNTING) {
		return [
			getTileIconName(FrameName.HUNTING_CAMP),
			getTileIconName(FrameName.HIGHLAND_HUNTER),
		];
	}
	return [];
});

const incomeValue = computed((): number => {
	const eventType = attributes.value.type;
	if (!isCollectableEvent(eventType)) return 0;
	const config = getEventCollectableConfig(eventType);
	return config.getReward(
		rare ? CollectableType.RARE : CollectableType.COMMON,
	);
});
</script>

<style lang="scss" scoped>
.eventium-production-box {
	position: relative;
	flex-shrink: 0;
	z-index: 1;
	width: 11.25rem;
	height: 4rem;

	&__icons {
		position: absolute;
		top: 0;
		width: 100%;
		height: 100%;
		@include flex-row-center;
		gap: 0.25rem;
	}
	&__arrow {
		margin-top: 0.15rem;
	}
	&__tile-icon {
		position: relative;
		.clicker-icon {
			position: absolute;
			bottom: -1rem;
			left: -1.2rem;
			transform: scaleX(-1);
		}
	}
	&__income {
		margin-bottom: 0.25rem;
		color: #372615;
		font-weight: bold;
		font-size: 1.5rem;
		&.rare {
			color: #0d6c27;
		}
	}
	&[data-event-type="easter"] {
		width: 10.5rem;
		height: 3rem;
		:deep(.eventium-box-background) {
			svg {
				width: 10.5rem;
				height: 3rem;
			}
		}
	}
	&[data-event-type="firefest"] {
		width: 10.5rem;
		height: 3rem;

		:deep(.eventium-box-background) {
			svg {
				width: 10.5rem;
				height: 3rem;
			}
		}
	}
	&[data-event-type="solarfair"] {
		width: 10.5rem;
		height: 3rem;

		:deep(.eventium-box-background) {
			svg {
				width: 10.5rem;
				height: 3rem;
			}
		}
	}
	&[data-event-type="halloween"] {
		width: 10.5rem;
		height: 3rem;
		:deep(.eventium-box-background) {
			svg {
				width: 10.5rem;
				height: 3rem;
			}
		}
	}
	&[data-event-type="pirates"] {
		width: 10.5rem;
		height: 3rem;
		:deep(.eventium-box-background) {
			svg {
				width: 10.5rem;
				height: 3rem;
			}
		}
	}
}
</style>
